import React, { useState, useRef, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../../../lib/api';
import {
  Search, Zap, Loader2, MapPin, Sun, ChevronDown, ChevronUp, Pause, Square,
  Play, Plus, X, ShieldCheck,
} from 'lucide-react';
import {
  getMaxPowerForInverters,
  getOptionModules,
  getOptionPower,
  optionUsesInverter,
  parseInverterModels,
  pickBestOption,
} from './autoFillUtils';

const TAXA_MIN = 50;
const RENDIMENTO = 0.76;
const DAYS = 30.44;

const formatBR = (v) => {
  if (v == null || isNaN(Number(v))) return '';
  return new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(v));
};

const AutoFillPanel = ({ getAuthHeaders, onRangesGenerated, currentRanges }) => {
  const [open, setOpen] = useState(false);
  const [cityQuery, setCityQuery] = useState('');
  const [citySuggestions, setCitySuggestions] = useState([]);
  const [selectedCity, setSelectedCity] = useState(null);
  const [loadingCities, setLoadingCities] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [kwhValue, setKwhValue] = useState('');
  const [rangeStart, setRangeStart] = useState('400');
  const [rangeEnd, setRangeEnd] = useState('1800');
  const [limitMode, setLimitMode] = useState('brl'); // 'brl' | 'modules' | 'inverters'
  const [maxModules, setMaxModules] = useState('30');
  const [inverterInput, setInverterInput] = useState('');
  const [ceilingInverters, setCeilingInverters] = useState([]);

  const [filling, setFilling] = useState(false);
  const [paused, setPaused] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [totalEstimated, setTotalEstimated] = useState(0);
  const [statusText, setStatusText] = useState('');
  const [apiCalls, setApiCalls] = useState(0);

  const abortRef = useRef(false);
  const pauseRef = useRef(false);
  const debounceRef = useRef(null);
  const suggestionsRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const searchCities = useCallback(async (q) => {
    if (!q || q.length < 2) { setCitySuggestions([]); return; }
    setLoadingCities(true);
    try {
      const resp = await axios.get(`${API}/solar/cities`, {
        params: { q },
        ...getAuthHeaders()
      });
      setCitySuggestions(Array.isArray(resp.data) ? resp.data.slice(0, 10) : []);
      setShowSuggestions(true);
    } catch { setCitySuggestions([]); }
    finally { setLoadingCities(false); }
  }, [getAuthHeaders]);

  const handleCityInput = (val) => {
    setCityQuery(val);
    setSelectedCity(null);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => searchCities(val), 400);
  };

  const selectCity = (city) => {
    setSelectedCity(city);
    setCityQuery(`${city.nome} - ${city.uf}`);
    setShowSuggestions(false);
    setCitySuggestions([]);
  };

  const waitIfPaused = () => new Promise((resolve) => {
    const check = () => {
      if (abortRef.current) { resolve(false); return; }
      if (!pauseRef.current) { resolve(true); return; }
      setTimeout(check, 200);
    };
    check();
  });

  const handlePause = () => { pauseRef.current = !pauseRef.current; setPaused(pauseRef.current); };
  const handleStop = () => { abortRef.current = true; pauseRef.current = false; setPaused(false); };

  // ─── Group options by module count, picking cheapest per group ───
  const groupByModules = (options) => {
    const map = {};
    for (const o of options) {
      const qty = o.quantidadeModulos || 0;
      if (!qty) continue;
      if (!map[qty] || o.valorFinal < map[qty].valorFinal) {
        map[qty] = o;
      }
    }
    return map; // { moduleCount: bestOption }
  };

  const buildRange = (best, radMedia, kwh) => {
    const potenciaUsina = best.potenciaParsed;
    const geracaoKwh = radMedia * RENDIMENTO * DAYS * potenciaUsina;
    const economia = (geracaoKwh * kwh) - ((geracaoKwh + TAXA_MIN) * kwh * 0.084);
    const consumptionMax = (geracaoKwh + TAXA_MIN) * kwh;
    return {
      label: `ate R$${Math.round(consumptionMax)}`,
      consumption_max: formatBR(consumptionMax),
      investment_value: formatBR(best.valorFinal),
      monthly_savings: formatBR(economia),
    };
  };

  const addCeilingInverters = () => {
    const nextModels = parseInverterModels([...ceilingInverters, inverterInput]);
    setCeilingInverters(nextModels);
    setInverterInput('');
  };

  const removeCeilingInverter = (modelToRemove) => {
    setCeilingInverters(models => models.filter(model => model !== modelToRemove));
  };

  const handleAutoFill = async () => {
    if (!selectedCity) { toast.error('Selecione uma cidade'); return; }
    const kwh = parseFloat(kwhValue.replace(',', '.'));
    if (!kwh || kwh <= 0) { toast.error('Informe o valor do kWh'); return; }
    const start = parseFloat(rangeStart);
    if (!start || start <= 0) { toast.error('Faixa inicial inválida'); return; }

    const useModuleLimit = limitMode === 'modules';
    const useInverterLimit = limitMode === 'inverters';
    const configuredInverters = parseInverterModels([...ceilingInverters, inverterInput]);
    const endBrl = limitMode === 'brl' ? parseFloat(rangeEnd) : Infinity;
    const endModules = useModuleLimit ? parseInt(maxModules) : Infinity;

    if (limitMode === 'brl' && (!endBrl || start >= endBrl)) { toast.error('Faixa final inválida'); return; }
    if (useModuleLimit && (!endModules || endModules < 1)) { toast.error('Qtd máxima de módulos inválida'); return; }
    if (useInverterLimit && !configuredInverters.length) { toast.error('Informe pelo menos um inversor teto'); return; }

    const radMedia = selectedCity.radMedia;
    const consumoKwhInitial = start / kwh;
    const potenciaInicial = (consumoKwhInitial - TAXA_MIN) / (radMedia * DAYS * RENDIMENTO);

    if (potenciaInicial <= 0) { toast.error('Faixa inicial muito baixa'); return; }

    const localizacao = {
      label: `${selectedCity.nome} - ${selectedCity.uf}`,
      value: selectedCity,
      fetchedAt: new Date().toISOString()
    };

    abortRef.current = false;
    pauseRef.current = false;
    setFilling(true);
    setPaused(false);
    setCurrentStep(0);
    setApiCalls(0);
    setTotalEstimated(
      useModuleLimit
        ? parseInt(maxModules)
        : useInverterLimit
          ? 20
          : Math.max(Math.ceil((endBrl - start) / 60), 5)
    );

    const newRanges = [];
    let moduleKwp = null;
    let currentPotencia = parseFloat(potenciaInicial.toFixed(3));
    let step = 0;
    let calls = 0;
    let currentModuleCount = null; // track actual module count
    let inverterCeilingFound = false;
    let inverterCeilingPower = null;

    // Cache of options from API calls keyed by moduleCount
    let cachedByModules = {};

    try {
      while (step < 80) {
        if (abortRef.current) { setStatusText('Interrompido'); break; }
        const canContinue = await waitIfPaused();
        if (!canContinue) { setStatusText('Interrompido'); break; }

        if (useModuleLimit && currentModuleCount !== null && currentModuleCount > endModules) break;

        step++;
        setCurrentStep(step);

        // Try to find an option in cache for the current target
        let best = null;

        if (currentModuleCount !== null) {
          const targetQty = currentModuleCount;
          // Use only the exact quantity. Accepting +1 here skipped the requested upper limit.
          if (cachedByModules[targetQty]) {
            const cached = cachedByModules[targetQty];
            delete cachedByModules[targetQty];

            const cachedPower = getOptionPower(cached);
            const exceedsModuleLimit = useModuleLimit && getOptionModules(cached) > endModules;
            const exceedsInverterLimit = useInverterLimit
              && inverterCeilingFound
              && cachedPower > inverterCeilingPower;

            if (!exceedsModuleLimit && !exceedsInverterLimit) {
              best = cached;
              console.log(`[AutoFill] Faixa ${step}: cache hit módulos=${targetQty} potência=${best.potenciaParsed} valor=${best.valorFinal}`);
            }
          }
        }

        // If not found in cache, make API call
        if (!best) {
          setStatusText(`Cotando faixa ${step}... (${currentPotencia.toFixed(2)} kWp) — chamada API`);

          const consumoKwh = (currentPotencia * radMedia * DAYS * RENDIMENTO) + TAXA_MIN;
          const payload = {
            localizacao,
            tensao: 'mono-220',
            consumoMensal: Math.round(consumoKwh),
            potenciaDesejada: parseFloat(currentPotencia.toFixed(3)),
            estrutura: 'telhado'
          };

          let data;
          try {
            const resp = await axios.post(`${API}/solar/cotacao`, payload, getAuthHeaders());
            data = resp.data;
            calls++;
            setApiCalls(calls);
          } catch (err) {
            toast.error(`Erro na cotação: ${err.response?.data?.detail || err.message}`);
            break;
          }

          const options = data.options || [];
          if (!options.length) { toast.warning(`Sem opções para ${currentPotencia.toFixed(2)} kWp`); break; }

          console.log(`[AutoFill] API call ${calls}: ${options.length} opções, potências: ${options.map(o => o.potenciaParsed).join(', ')}`);

          if (useInverterLimit) {
            const matchingCeilingPower = getMaxPowerForInverters(options, configuredInverters);
            if (matchingCeilingPower != null) {
              inverterCeilingFound = true;
              inverterCeilingPower = Math.max(inverterCeilingPower ?? 0, matchingCeilingPower);
              setTotalEstimated(Math.max(step, Math.ceil(inverterCeilingPower / (moduleKwp || 0.55))));
            }
          }

          best = pickBestOption(options, currentPotencia, {
            targetModules: currentModuleCount,
            maxModules: endModules,
            maxPower: useInverterLimit && inverterCeilingFound ? inverterCeilingPower : Infinity,
          });

          // Once the configured inverter tier was seen, an empty result means every
          // available next kit is already above its maximum plant power.
          if (!best && useInverterLimit && inverterCeilingFound) break;
          if (!best) { toast.warning(`Sem opções válidas para ${currentPotencia.toFixed(2)} kWp`); break; }

          console.log(`[AutoFill] Faixa ${step}: selecionado módulos=${best.quantidadeModulos} potência=${best.potenciaParsed} valor=${best.valorFinal}`);

          // Cache ALL other options grouped by module count
          const optionsWithinLimit = options.filter((option) => {
            const optionPower = getOptionPower(option);
            return getOptionModules(option) <= endModules
              && (!useInverterLimit
                || !inverterCeilingFound
                || optionPower <= inverterCeilingPower);
          });
          const grouped = groupByModules(optionsWithinLimit);
          const bestQty = best.quantidadeModulos || 0;
          for (const [qty, opt] of Object.entries(grouped)) {
            const q = parseInt(qty);
            if (q > bestQty) { // only cache HIGHER module counts (future ranges)
              cachedByModules[q] = opt;
            }
          }
        } else {
          setStatusText(`Faixa ${step} — reaproveitando cotação (${best.potenciaParsed?.toFixed(2)} kWp)`);
        }

        const potenciaUsina = getOptionPower(best);
        const qtdModulos = getOptionModules(best);

        if (useModuleLimit && qtdModulos > endModules) break;
        if (useInverterLimit && optionUsesInverter(best, configuredInverters)) {
          inverterCeilingFound = true;
          inverterCeilingPower = Math.max(inverterCeilingPower ?? 0, potenciaUsina);
        }
        if (useInverterLimit
          && inverterCeilingFound
          && potenciaUsina > inverterCeilingPower) break;

        if (moduleKwp === null && qtdModulos > 0) {
          moduleKwp = potenciaUsina / qtdModulos;
          if (useModuleLimit) {
            setTotalEstimated(Math.max(endModules - qtdModulos + 1, 1));
          } else if (!useInverterLimit) {
            const endPotencia = (endBrl / kwh - TAXA_MIN) / (radMedia * DAYS * RENDIMENTO);
            const est = Math.ceil((endPotencia - potenciaInicial) / moduleKwp) + 1;
            setTotalEstimated(Math.max(est, step));
          }
        }

        newRanges.push(buildRange(best, radMedia, kwh));
        onRangesGenerated([...newRanges]);

        // ─── Advance to next range ───
        if (!moduleKwp || moduleKwp <= 0) break;

        // Track by module count to avoid drift
        currentModuleCount = qtdModulos + 1;
        currentPotencia = parseFloat((currentModuleCount * moduleKwp).toFixed(3));

        // Module limit check
        if (useModuleLimit && currentModuleCount > endModules) break;

        // BRL limit check
        if (limitMode === 'brl') {
          const nextGeracao = radMedia * RENDIMENTO * DAYS * currentPotencia;
          const nextConsumoBrl = (nextGeracao + TAXA_MIN) * kwh;
          if (nextConsumoBrl > endBrl * 1.15) break;
        }
      }

      if (!abortRef.current) {
        if (useInverterLimit && !inverterCeilingFound) {
          setStatusText(`Teto não encontrado — ${newRanges.length} faixas geradas`);
          toast.warning('Os inversores teto não foram encontrados nas cotações');
        } else {
          const ceilingSuffix = useInverterLimit ? ' · teto por inversor aplicado' : '';
          setStatusText(`Concluído — ${newRanges.length} faixas, ${calls} chamadas API${ceilingSuffix}`);
          toast.success(`${newRanges.length} faixas geradas (${calls} chamadas API)`);
        }
      }
    } catch (err) {
      toast.error('Erro inesperado: ' + err.message);
    } finally {
      setFilling(false);
      setPaused(false);
    }
  };

  const progressPct = totalEstimated > 0 ? Math.min((currentStep / totalEstimated) * 100, 100) : 0;

  return (
    <div className="border border-blue-200 rounded-xl bg-gradient-to-r from-blue-50/50 to-indigo-50/50 overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between p-4 hover:bg-blue-50/50 transition-colors"
      >
        <div className="flex items-center gap-2">
          <Zap className="w-5 h-5 text-blue-600" />
          <span className="font-semibold text-blue-800 text-sm">Preenchimento Automático</span>
          <span className="text-xs text-blue-500 bg-blue-100 px-2 py-0.5 rounded-full">via API Solar</span>
        </div>
        {open ? <ChevronUp className="w-4 h-4 text-blue-400" /> : <ChevronDown className="w-4 h-4 text-blue-400" />}
      </button>

      {open && (
        <div className="p-4 pt-0 space-y-4">
          {/* City Search */}
          <div className="relative" ref={suggestionsRef}>
            <label className="text-xs font-medium text-gray-600 mb-1 block">Cidade (radiação solar)</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text" value={cityQuery}
                onChange={(e) => handleCityInput(e.target.value)}
                onFocus={() => citySuggestions.length > 0 && setShowSuggestions(true)}
                placeholder="Digite o nome da cidade..."
                disabled={filling}
                className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
              />
              {loadingCities && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-blue-500 animate-spin" />}
            </div>
            {showSuggestions && citySuggestions.length > 0 && (
              <div className="absolute z-20 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                {citySuggestions.map((c) => (
                  <button key={c._id || c.id} type="button" onClick={() => selectCity(c)}
                    className="w-full text-left px-3 py-2 hover:bg-blue-50 text-sm flex items-center justify-between border-b last:border-0">
                    <span className="flex items-center gap-2">
                      <MapPin className="w-3.5 h-3.5 text-gray-400" />
                      <span className="font-medium text-gray-800">{c.nome}</span>
                      <span className="text-gray-400">- {c.uf}</span>
                    </span>
                    <span className="flex items-center gap-1 text-xs text-amber-600">
                      <Sun className="w-3 h-3" />{c.radMedia?.toFixed(2)}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {selectedCity && (
            <div className="bg-white rounded-lg border border-green-200 p-3 flex items-center gap-3">
              <Sun className="w-5 h-5 text-amber-500" />
              <div>
                <p className="text-sm font-semibold text-gray-800">{selectedCity.nome} - {selectedCity.uf}</p>
                <p className="text-xs text-gray-500">
                  Radiação média: <strong className="text-amber-600">{selectedCity.radMedia?.toFixed(3)} kWh/m²/dia</strong>
                  {selectedCity.concessionaria && <> · {selectedCity.concessionaria.sigla}</>}
                </p>
              </div>
            </div>
          )}

          {/* Parameters */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">Valor do kWh (R$)</label>
              <input type="text" inputMode="decimal" value={kwhValue}
                onChange={(e) => setKwhValue(e.target.value)} placeholder="0,98" disabled={filling}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 disabled:opacity-50" />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">Faixa Inicial (R$)</label>
              <input type="number" value={rangeStart}
                onChange={(e) => setRangeStart(e.target.value)} placeholder="400" disabled={filling}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 disabled:opacity-50" />
            </div>
          </div>

          {/* Upper limit mode */}
          <div>
            <label className="text-xs font-medium text-gray-600 mb-1.5 block">Limite Superior</label>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-2" role="group" aria-label="Tipo de limite superior">
              <button type="button" disabled={filling}
                onClick={() => setLimitMode('brl')}
                aria-pressed={limitMode === 'brl'}
                className={`min-h-10 text-xs px-3 py-2 rounded-lg font-medium transition-colors ${
                  limitMode === 'brl'
                    ? 'bg-blue-600 text-white shadow-sm'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                } disabled:opacity-50`}>
                Valor Mensal (R$)
              </button>
              <button type="button" disabled={filling}
                onClick={() => setLimitMode('modules')}
                aria-pressed={limitMode === 'modules'}
                className={`min-h-10 text-xs px-3 py-2 rounded-lg font-medium transition-colors ${
                  limitMode === 'modules'
                    ? 'bg-blue-600 text-white shadow-sm'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                } disabled:opacity-50`}>
                Qtd. de Módulos
              </button>
              <button type="button" disabled={filling}
                onClick={() => setLimitMode('inverters')}
                aria-pressed={limitMode === 'inverters'}
                className={`min-h-10 text-xs px-3 py-2 rounded-lg font-medium transition-colors flex items-center justify-center gap-1.5 ${
                  limitMode === 'inverters'
                    ? 'bg-blue-600 text-white shadow-sm'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                } disabled:opacity-50`}>
                <ShieldCheck className="w-3.5 h-3.5" />Inversor Teto
              </button>
            </div>
            {limitMode === 'brl' ? (
              <input type="number" value={rangeEnd}
                onChange={(e) => setRangeEnd(e.target.value)} placeholder="1800" disabled={filling}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 disabled:opacity-50" />
            ) : limitMode === 'modules' ? (
              <div className="relative">
                <input type="number" value={maxModules} min="1" max="100"
                  onChange={(e) => setMaxModules(e.target.value)} placeholder="30" disabled={filling}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 disabled:opacity-50" />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">módulos</span>
              </div>
            ) : (
              <div className="rounded-lg border border-blue-100 bg-white/80 p-3 space-y-2.5">
                <div>
                  <label htmlFor="inverter-ceiling" className="text-xs font-medium text-gray-700 block">
                    Modelos de inversor teto
                  </label>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Adicione um ou mais códigos exatamente como aparecem nos kits.
                  </p>
                </div>
                <div className="flex flex-col sm:flex-row gap-2">
                  <input
                    id="inverter-ceiling"
                    type="text"
                    value={inverterInput}
                    onChange={(e) => setInverterInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ',') {
                        e.preventDefault();
                        addCeilingInverters();
                      }
                    }}
                    placeholder="Ex.: PHB7K5-MS"
                    disabled={filling}
                    className="flex-1 min-h-10 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                  />
                  <button
                    type="button"
                    onClick={addCeilingInverters}
                    disabled={filling || !inverterInput.trim()}
                    className="min-h-10 px-3 py-2 border border-blue-200 bg-blue-50 text-blue-700 rounded-lg text-xs font-semibold hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-1.5 transition-colors"
                  >
                    <Plus className="w-3.5 h-3.5" />Adicionar
                  </button>
                </div>
                {ceilingInverters.length > 0 && (
                  <div className="flex flex-wrap gap-1.5" aria-label="Inversores teto configurados">
                    {ceilingInverters.map(model => (
                      <span key={model} className="inline-flex min-h-8 items-center gap-1.5 rounded-md bg-blue-100 px-2.5 py-1 text-xs font-semibold text-blue-800">
                        {model}
                        <button
                          type="button"
                          onClick={() => removeCeilingInverter(model)}
                          disabled={filling}
                          aria-label={`Remover inversor ${model}`}
                          className="-mr-1 flex h-6 w-6 items-center justify-center rounded hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
                <p className="text-xs leading-relaxed text-gray-500">
                  A tabela inclui as faixas até a maior potência encontrada com qualquer um desses inversores. Kits posteriores, com outro inversor, são ignorados.
                </p>
              </div>
            )}
          </div>

          {/* Progress */}
          {filling && (
            <div className="space-y-2">
              <div className="w-full bg-gray-200 rounded-full h-2.5 overflow-hidden">
                <div className="h-full bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full transition-all duration-500 ease-out"
                  style={{ width: `${progressPct}%` }} />
              </div>
              <div className="flex items-center justify-between">
                <p className="text-xs text-blue-600 font-medium">{statusText}</p>
                <p className="text-xs text-gray-400">
                  Faixa {currentStep} · {apiCalls} chamada{apiCalls !== 1 ? 's' : ''} API
                </p>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-2">
            {!filling ? (
              <button type="button" onClick={handleAutoFill} disabled={!selectedCity}
                className="flex-1 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white rounded-lg text-sm font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2">
                <Zap className="w-4 h-4" />Preencher Tabela
              </button>
            ) : (
              <>
                <button type="button" onClick={handlePause}
                  className={`flex-1 py-2.5 rounded-lg text-sm font-semibold transition-all flex items-center justify-center gap-2 ${
                    paused ? 'bg-green-600 hover:bg-green-700 text-white' : 'bg-amber-500 hover:bg-amber-600 text-white'
                  }`}>
                  {paused ? <><Play className="w-4 h-4" />Continuar</> : <><Pause className="w-4 h-4" />Pausar</>}
                </button>
                <button type="button" onClick={handleStop}
                  className="px-5 py-2.5 bg-red-500 hover:bg-red-600 text-white rounded-lg text-sm font-semibold transition-all flex items-center justify-center gap-2">
                  <Square className="w-4 h-4" />Parar
                </button>
              </>
            )}
          </div>

          {!filling && statusText && <p className="text-xs text-center text-gray-500">{statusText}</p>}
          <p className="text-xs text-gray-400 text-center">
            Tensão: mono-220 · Estrutura: telhado · Eficiência: 76% · Taxa mín: 50 kWh
          </p>
        </div>
      )}
    </div>
  );
};

export default AutoFillPanel;
